import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, message, Modal, Form,
  Typography, Radio, Tooltip, Tag, Divider, Alert, Upload,
  Row, Col, Menu, Dropdown, Popconfirm, Empty
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ExclamationCircleOutlined,
  ArrowLeftOutlined, SearchOutlined,
  CloudUploadOutlined, DownOutlined, EditOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import { validateCIDR } from '../../../shared/utils/validation';
import Papa from 'papaparse';

import { userPath } from '../../../shared/config/paths';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const DnsCidrEntries = () => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cidr, setCidr] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState(t('Add CIDR entry'));
  const [editingEntry, setEditingEntry] = useState(null);
  const [form] = Form.useForm();
  const [bulkForm] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [ipVersionFilter, setIpVersionFilter] = useState('all');
  const navigate = useNavigate();
  const { cidrId } = useParams();

  // 获取CIDR条目列表
  const fetchEntries = async () => {
    setLoading(true);
    try {
      const result = await dnsService.getCidrEntries(cidrId, searchValue);
      if (result.success) {
        setEntries(result.data.entries);
        setCidr(result.data.cidr);
      } else {
        message.error(result.error || t('Failed to retrieve CIDR entries'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve CIDR entries: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [cidrId, searchValue]);

  // 显示添加条目模态框
  const showAddModal = () => {
    setModalTitle(t('Add CIDR entry'));
    setEditingEntry(null);
    form.resetFields();
    form.setFieldsValue({
      cidr_id: parseInt(cidrId),
      ip_version: 'both'
    });
    setIsModalVisible(true);
  };

  // 显示编辑条目模态框
  const showEditModal = (entry) => {
    setModalTitle(t('Edit CIDR entry: {{cidr}}', { cidr: entry.cidr }));
    setEditingEntry(entry);
    form.resetFields();
    form.setFieldsValue({
      cidr_id: entry.cidr_id,
      cidr: entry.cidr,
      description: entry.description || '',
      ip_version: entry.ip_version || 'both'
    });
    setIsModalVisible(true);
  };

  // 处理模态框提交
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // 验证CIDR格式
      if (!validateCIDR(values.cidr)) {
        message.error(t('Invalid CIDR format'));
        return;
      }

      // 检查IP版本是否与CIDR格式匹配
      const isIPv6 = values.cidr.includes(':');
      if ((values.ip_version === 'ipv4' && isIPv6) || (values.ip_version === 'ipv6' && !isIPv6)) {
        message.error(t('IP version mismatch. You selected {{value}} but provided a {{value2}} address.', { value: values.ip_version.toUpperCase(), value2: isIPv6 ? 'IPv6' : 'IPv4' }));
        return;
      }

      setLoading(true);

      if (editingEntry) {
        // 更新条目
        const result = await dnsService.updateCidrEntry({
          id: editingEntry.id,
          ...values
        });

        if (result.success) {
          message.success(t('CIDR entry updated'));
          setIsModalVisible(false);
          fetchEntries();
        } else {
          message.error(result.error || t('Failed to update CIDR entry'));
        }
      } else {
        // 创建新条目
        const result = await dnsService.createCidrEntry(values);

        if (result.success) {
          message.success(t('CIDR entry added'));
          setIsModalVisible(false);
          fetchEntries();
        } else {
          message.error(result.error || t('Failed to add CIDR entry'));
        }
      }
    } catch (error) {
      console.log('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 删除条目
  const deleteEntry = (entry) => {
    confirm({
      title: t('Confirm deletion'),
      icon: <ExclamationCircleOutlined />,
      content: t('Delete CIDR entry "{{cidr}}"? This action cannot be undone.', { cidr: entry.cidr }),
      okText: t('Delete'),
      okType: 'danger',
      cancelText: t('Cancel'),
      onOk: async () => {
        try {
          setLoading(true);
          const result = await dnsService.deleteCidrEntry(entry.id);

          if (result.success) {
            message.success(t('CIDR entry deleted'));
            fetchEntries();
          } else {
            message.error(result.error || t('Failed to delete CIDR entry'));
          }
        } catch (error) {
          message.error(t('Failed to delete CIDR entry: {{message}}', { message: error.message }));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 批量删除条目
  const bulkDeleteEntries = async () => {
    if (selectedEntries.length === 0) {
      message.warning(t('Select the entries to delete'));
      return;
    }

    confirm({
      title: t('Confirm bulk deletion'),
      icon: <ExclamationCircleOutlined />,
      content: t('Delete the {{selectedEntriesCount}} selected CIDR entries? This action cannot be undone.', { selectedEntriesCount: selectedEntries.length }),
      okText: t('Delete'),
      okType: 'danger',
      cancelText: t('Cancel'),
      onOk: async () => {
        setLoading(true);

        try {
          // 创建所有删除操作的Promise
          const deletePromises = selectedEntries.map(id =>
            dnsService.deleteCidrEntry(id)
          );

          // 等待所有删除操作完成
          const results = await Promise.all(deletePromises);

          // 计算成功和失败的数量
          const successCount = results.filter(r => r.success).length;
          const failedCount = results.length - successCount;

          if (failedCount === 0) {
            message.success(t('Deleted {{successCount}} CIDR entries', { successCount }));
          } else {
            message.warning(t('Deleted {{successCount}} entries; {{failedCount}} failed to delete', { successCount, failedCount }));
          }

          // 清除选择
          setSelectedEntries([]);
          // 重新加载数据
          fetchEntries();
        } catch (error) {
          message.error(t('Bulk deletion failed: {{message}}', { message: error.message }));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 批量更改IP版本
  const bulkChangeIpVersion = (version) => {
    if (selectedEntries.length === 0) {
      message.warning(t('Select the entries to update'));
      return;
    }

    confirm({
      title: t('Bulk update IP version'),
      icon: <ExclamationCircleOutlined />,
      content: t('Change the IP version of the {{selectedEntriesCount}} selected CIDR entries to {{value}}?', { selectedEntriesCount: selectedEntries.length, value: version === 'ipv4' ? 'IPv4' : version === 'ipv6' ? 'IPv6' : t('Auto-detect') }),
      okText: t('Update'),
      cancelText: t('Cancel'),
      onOk: async () => {
        setLoading(true);

        try {
          // 创建所有更新操作的Promise
          const updatePromises = selectedEntries.map(id =>
            dnsService.updateCidrEntry({
              id,
              ip_version: version
            })
          );

          // 等待所有更新操作完成
          const results = await Promise.all(updatePromises);

          // 计算成功和失败的数量
          const successCount = results.filter(r => r.success).length;
          const failedCount = results.length - successCount;

          if (failedCount === 0) {
            message.success(t('Updated the IP version for {{successCount}} CIDR entries', { successCount }));
          } else {
            message.warning(t('Updated {{successCount}} entries; {{failedCount}} failed to update', { successCount, failedCount }));
          }

          // 清除选择
          setSelectedEntries([]);
          // 重新加载数据
          fetchEntries();
        } catch (error) {
          message.error(t('Bulk update failed: {{message}}', { message: error.message }));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // 处理搜索
  const handleSearch = (value) => {
    setSearchValue(value);
  };

  // 处理IP版本筛选
  const handleIpVersionFilter = (version) => {
    setIpVersionFilter(version);
  };

  // 显示批量导入模态框
  const showBulkImportModal = () => {
    bulkForm.resetFields();
    setIsBulkModalVisible(true);
  };

  // 处理文件上传
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      bulkForm.setFieldsValue({
        entries: e.target.result
      });
    };
    reader.readAsText(file);
    return false; // 阻止自动上传
  };

  // 处理批量导入提交
  const handleBulkImportOk = async () => {
    try {
      const values = await bulkForm.validateFields();
      const entries = values.entries.trim();

      if (!entries) {
        message.error(t('Enter the CIDR entries to import'));
        return;
      }

      // 尝试解析CSV或文本格式
      let cidrEntries = [];

      try {
        // 尝试使用Papa Parse解析CSV
        const parseResult = Papa.parse(entries, { header: false, skipEmptyLines: true });

        if (parseResult.errors.length > 0) {
          throw new Error('CSV解析错误');
        }

        cidrEntries = parseResult.data.map(row => {
          if (row.length < 1) {
            throw new Error('格式错误: 每行至少需要一个CIDR值');
          }

          return {
            cidr: row[0].trim(),
            description: row.length > 1 ? row[1].trim() : '',
            ip_version: row.length > 2 ? row[2].trim() : 'both'
          };
        });
      } catch (csvError) {
        // 如果CSV解析失败，尝试按行分割
        cidrEntries = entries.split('\n')
          .filter(line => line.trim() !== '')
          .map(line => {
            const parts = line.split('|').map(part => part.trim());

            return {
              cidr: parts[0],
              description: parts.length > 1 ? parts[1] : '',
              ip_version: parts.length > 2 ? parts[2] : 'both'
            };
          });
      }

      // 验证CIDR格式
      const invalidEntries = cidrEntries.filter(entry => !validateCIDR(entry.cidr));
      if (invalidEntries.length > 0) {
        message.error(t('Found {{invalidEntriesCount}} entries with invalid CIDR format', { invalidEntriesCount: invalidEntries.length }));
        return;
      }

      // 验证IP版本
      const mismatchEntries = cidrEntries.filter(entry => {
        if (entry.ip_version === 'both') return false;

        const isIPv6 = entry.cidr.includes(':');
        return (entry.ip_version === 'ipv4' && isIPv6) || (entry.ip_version === 'ipv6' && !isIPv6);
      });

      if (mismatchEntries.length > 0) {
        message.error(t('Found {{mismatchEntriesCount}} entries with mismatched IP versions and CIDR formats', { mismatchEntriesCount: mismatchEntries.length }));
        return;
      }

      setLoading(true);

      // 批量创建条目
      const createPromises = cidrEntries.map(entry =>
        dnsService.createCidrEntry({
          cidr_id: parseInt(cidrId),
          ...entry
        })
      );

      const results = await Promise.all(createPromises);

      // 计算成功和失败的数量
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        message.success(t('Imported {{successCount}} CIDR entries', { successCount }));
        setIsBulkModalVisible(false);
        fetchEntries();
      } else {
        message.warning(t('Imported {{successCount}} entries; {{failedCount}} failed to import', { successCount, failedCount }));
      }
    } catch (error) {
      console.log('Validation failed:', error);
      message.error(t('Import failed: {{message}}', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  // 处理表格选择变化
  const handleSelectionChange = (selectedRowKeys) => {
    setSelectedEntries(selectedRowKeys);
  };

  // 处理全选
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // 根据当前筛选器选择所有可见条目
      const visibleEntries = entries.filter(entry => {
        if (ipVersionFilter === 'all') return true;
        if (ipVersionFilter === 'ipv4') return !entry.cidr.includes(':');
        if (ipVersionFilter === 'ipv6') return entry.cidr.includes(':');
        return true;
      });
      setSelectedEntries(visibleEntries.map(entry => entry.id));
    } else {
      setSelectedEntries([]);
    }
  };

  // IP版本筛选菜单
  const ipVersionMenu = (
    <Menu onClick={({ key }) => handleIpVersionFilter(key)}>
      <Menu.Item key="all">{t('All IP versions')}</Menu.Item>
      <Menu.Item key="ipv4">{t('IPv4 only')}</Menu.Item>
      <Menu.Item key="ipv6">{t('IPv6 only')}</Menu.Item>
    </Menu>
  );

  // 批量操作菜单
  const bulkActionMenu = (
    <Menu>
      <Menu.Item key="delete" onClick={bulkDeleteEntries} danger>
        {t('Delete selected entries')}
      </Menu.Item>
      <Menu.SubMenu title={t('Change IP version')}>
        <Menu.Item key="both" onClick={() => bulkChangeIpVersion('both')}>{t('Auto-detect')}</Menu.Item>
        <Menu.Item key="ipv4" onClick={() => bulkChangeIpVersion('ipv4')}>IPv4</Menu.Item>
        <Menu.Item key="ipv6" onClick={() => bulkChangeIpVersion('ipv6')}>IPv6</Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  // 是否可编辑（非系统预设CIDR才可编辑）
  const isEditable = cidr?.editable !== false;

  // 表格列配置
  const columns = [
    {
      title: 'CIDR',
      dataIndex: 'cidr',
      key: 'cidr',
      render: (text, record) => (
        <Space>
          <Text copyable={{ text, tooltips: [t('Copy'), t('Copied!')] }}>{text}</Text>
          {record.cidr.includes(':') ? (
            <Tag color="blue">IPv6</Tag>
          ) : (
            <Tag color="green">IPv4</Tag>
          )}
        </Space>
      ),
      filteredValue: ipVersionFilter === 'all' ? null : [ipVersionFilter],
      onFilter: (value, record) => {
        if (value === 'ipv4') return !record.cidr.includes(':');
        if (value === 'ipv6') return record.cidr.includes(':');
        return true;
      },
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      render: text => text || '-'
    },
    {
      title: t('IP version'),
      dataIndex: 'ip_version',
      key: 'ip_version',
      render: version => {
        if (!version || version === 'both') return <span>{t('Auto-detect')}</span>;
        return <Tag color={version === 'ipv4' ? 'green' : 'blue'}>{version.toUpperCase()}</Tag>;
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: text => new Date(text).toLocaleString()
    },
    ...(isEditable ? [{
      title: t('Actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            {t('Edit')}
          </Button>
          <Popconfirm
            title={t('Delete this CIDR entry?')}
            onConfirm={() => deleteEntry(record)}
            okText={t('Yes')}
            cancelText={t('No')}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            >
              {t('Delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ];

  // 决定要显示哪些条目（基于IP版本筛选）
  const filteredEntries = ipVersionFilter === 'all'
    ? entries
    : entries.filter(entry => {
        const isIPv6 = entry.cidr.includes(':');
        return (ipVersionFilter === 'ipv4' && !isIPv6) || (ipVersionFilter === 'ipv6' && isIPv6);
      });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>{t('CIDR entry {{value}}: {{value2}}', { value: isEditable ? t('Manage') : t('View'), value2: cidr?.name || '' })}</Title>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(userPath('/dns/cidr'))}
          >
            {t('Back to CIDR lists')}
          </Button>
        </Space>
      </div>

      {cidr?.description && (
        <Alert
          message={cidr.description}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!isEditable && (
        <Alert
          message={t('System preset CIDR list, view only; cannot be edited')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input.Search
              placeholder={t('Search CIDR or description')}
              allowClear
              enterButton
              onSearch={handleSearch}
            />
          </Col>
          <Col>
            <Dropdown overlay={ipVersionMenu}>
              <Button>
                {t('Filter by IP version')} <DownOutlined />
              </Button>
            </Dropdown>
          </Col>
          {isEditable && (
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showAddModal}
              >
                {t('Add entry')}
              </Button>
            </Col>
          )}
          {isEditable && (
            <Col>
              <Button
                icon={<CloudUploadOutlined />}
                onClick={showBulkImportModal}
              >
                {t('Bulk import')}
              </Button>
            </Col>
          )}
        </Row>

        {selectedEntries.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={
                <Space>
                  <span>{t('{{selectedEntriesCount}} entries selected', { selectedEntriesCount: selectedEntries.length })}</span>
                  <Dropdown overlay={bulkActionMenu}>
                    <Button size="small">
                      {t('Bulk actions')} <DownOutlined />
                    </Button>
                  </Dropdown>
                </Space>
              }
              type="info"
              showIcon
              closable
              onClose={() => setSelectedEntries([])}
            />
          </div>
        )}

        <Table
          rowSelection={isEditable ? {
            selectedRowKeys: selectedEntries,
            onChange: handleSelectionChange
          } : undefined}
          columns={columns}
          dataSource={filteredEntries}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: <Empty description={t('No CIDR entries yet')} />
          }}
          pagination={{
            showSizeChanger: true,
            showTotal: total => t('{{total}} entries total', { total })
          }}
        />
      </Card>

      {/* 添加/编辑条目模态框 */}
      <Modal
        title={modalTitle}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="cidr_id"
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="cidr"
            label="CIDR"
            rules={[
              { required: true, message: t('Please enter a CIDR!') },
              {
                validator: (_, value) => {
                  if (!value || validateCIDR(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('Please enter valid CIDR notation, such as 192.168.1.0/24 or 2001:db8::/64')));
                }
              }
            ]}
          >
            <Input placeholder={t('E.g. 192.168.1.0/24 or 2001:db8::/64')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('Description (optional)')}
          >
            <Input placeholder={t('E.g. company intranet or China Telecom')} />
          </Form.Item>

          <Form.Item
            name="ip_version"
            label={t('IP version')}
          >
            <Radio.Group>
              <Radio value="both">{t('Auto-detect')}</Radio>
              <Radio value="ipv4">IPv4</Radio>
              <Radio value="ipv6">IPv6</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入模态框 */}
      <Modal
        title={t('Bulk import CIDR entries')}
        open={isBulkModalVisible}
        onOk={handleBulkImportOk}
        onCancel={() => setIsBulkModalVisible(false)}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={bulkForm}
          layout="vertical"
        >
          <Alert
            message={t('Bulk import format guide')}
            description={
              <div>
                <p>{t('One entry per line, in this format:')}</p>
                <Text code>{t('CIDR|Description|IP version')}</Text>
                <p>{t('Including:')}</p>
                <ul>
                  <li>{t('CIDR is required, e.g., 192.168.1.0/24 or 2001:db8::/64')}</li>
                  <li>{t('Description is optional')}</li>
                  <li>{t('IP version is optional. Valid values: both (auto-detect), ipv4, ipv6')}</li>
                </ul>
                <p>{t('Example:')}</p>
                <Text code>{t('192.168.1.0/24|Company intranet|ipv4')}</Text>
                <Text code>{t('2001:db8::/64|IPv6 test network')}</Text>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="entries"
            label={t('CIDR entries')}
            rules={[{ required: true, message: t('Enter the CIDR entries to import!') }]}
          >
            <TextArea rows={10} placeholder={t('One CIDR entry per line')} />
          </Form.Item>

          <Form.Item>
            <Upload.Dragger
              name="file"
              beforeUpload={handleFileUpload}
              showUploadList={false}
              accept=".txt,.csv"
            >
              <p className="ant-upload-drag-icon">
                <CloudUploadOutlined />
              </p>
              <p className="ant-upload-text">{t('Click or drag files here to upload')}</p>
              <p className="ant-upload-hint">{t('Supports .txt or .csv files')}</p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DnsCidrEntries;
